import { defineComponent, onMounted, computed, watch, ref, reactive, inject } from '@vue/composition-api';
import { isEqual, cloneDeep } from 'lodash';
import { fetchThemes, useFetchData, getQueryRows, isQueryValid, makeNewThemeName, getConceptsFromQuery, } from './ThemeBuilder.utils';
import { QueryLocation } from 'src/types/Query.types';
import { BfButton, BfSpinner } from 'components/Butterfly';
import QueryUtils, { hasUnstructured, expandQuery, countTextRows } from 'src/utils/query';
import Query, { expandQueryIfPossible, expandSavedQueryIfPossible, ThemeGroup } from 'src/api/query';
import ProjectAPI from 'src/api/project';
import VerbatimsWidget from 'components/DataWidgets/VerbatimsWidget/VerbatimsWidget.vue';
import ContextNetwork from 'src/components/DataWidgets/ContextNetwork/ContextNetwork.vue';
import KeyPhrases from 'src/components/DataWidgets/KeyPhrases/KeyPhrases.vue';
import ThemesTab from 'src/components/project/analysis/results/ThemeBuilder/ThemesTab.vue';
import ThemesTree from 'src/components/project/analysis/results/ThemeBuilder/ThemesTree.vue';
import ConceptList from './ConceptList.vue';
import { fetch_keyphrases_data, fetch_pivot_data } from 'src/store/modules/data/api';
import ThemeNameModal from './ThemeNameModal.vue';
import ThemeDashboardModal from './ThemeDashboardModal.vue';
import ThemeDeleteModal from './ThemeDeleteModal.vue';
import { useStore } from 'src/store';
import { CLEAR_REQUEST_ERRORS, SET_SAVED_QUERIES } from 'src/store/types';
import StoryboardPage from 'src/components/project/analysis/results/Storyboard.vue';
import Dropdown from 'components/Butterfly/Dropdown/Dropdown.vue';
import DropdownItem from 'components/Butterfly/Dropdown/DropdownItem.vue';
import Icon from 'src/components/Icon.vue';
import FloatingPanel from 'components/widgets/FloatingPanel/FloatingPanel.vue';
import ClickedOutside from 'src/components/ClickedOutside.vue';
import { comma, percent } from 'src/utils/formatters';
import ConfirmModal from 'components/ConfirmModal.vue';
import ToggleSliderInput from 'src/components/forms/ToggleSliderInput.vue';
import ProgressBar from './ProgressBar.vue';
import { useRouter } from 'src/router';
import FilterBar from './FilterBar.vue';
const ERROR = {
    FETCH_THEMES: 'Failed to fetch themes.',
};
const ThemeBuilder = defineComponent({
    components: {
        BfButton,
        BfSpinner,
        VerbatimsWidget,
        ContextNetwork,
        KeyPhrases,
        ThemesTab,
        ConceptList,
        ThemeNameModal,
        ThemeDeleteModal,
        StoryboardPage,
        Dropdown,
        DropdownItem,
        Icon,
        ThemeDashboardModal,
        FloatingPanel,
        ClickedOutside,
        ConfirmModal,
        ToggleSliderInput,
        ProgressBar,
        FilterBar,
        ThemesTree,
    },
    provide: {
        isOnDashboard: false,
    },
    beforeRouteLeave(to, from, next) {
        if (this.isUnsaved) {
            const confirm = window.confirm('You have unsaved changes. Are you sure you want to leave?');
            confirm ? next() : next(false);
        }
        else {
            next();
        }
    },
    props: {
        analysisId: { type: Number, required: true },
        projectId: { type: Number, required: true },
        currentModel: { type: Object, required: true },
        currentAnalysis: { type: Object, required: true },
        currentSite: { type: Object, required: true },
        currentProject: { type: Object, required: true },
        currentUser: { type: Object, required: true },
        featureFlags: { type: Object, required: true },
        state: { type: String, required: false, default: '' },
        hasNPS: { type: Boolean, required: false, default: false },
        hasSentiment: { type: Boolean, required: false, default: false },
        hasDate: { type: Boolean, required: false, default: false },
    },
    setup(props) {
        const store = useStore();
        const router = useRouter();
        const analytics = inject('analytics');
        const resetConfirmModal = ref();
        const error = ref(null);
        const fetchedData = reactive({
            'progress-coverage': {},
            'themes-coverage': {},
            'key-phrases': {},
            'context-network': {},
            'verbatims': {},
            'ignored-concepts-stats': {},
        });
        const activeTab = ref('themes');
        const verbatimsConfig = ref({
            name: 'verbatims',
            visible: true,
            options: {
                perPage: 50,
                orderBy: "most_relevant"
            }
        });
        const showThemeNameModal = ref(false);
        const showDashboardModal = ref(false);
        const showThemeDeleteModal = ref(false);
        const scrollableContent = ref(null);
        const showJSON = ref(false);
        const submitErrors = ref([]);
        const isLoading = reactive({
            state: true,
            themes: false,
            coverage: false,
            keyConcepts: false,
            stats: false,
            ignoredConceptsStats: false,
        });
        const queryStats = reactive({
            record_count: 0,
            verbatim_count: 0,
            total_record_count: 0,
            total_verbatim_count: 0,
            ignored_verbatim_count: 0,
        });
        const hasNoResults = computed(() => {
            return stagedQueryIsValid.value && queryStats.record_count === 0;
        });
        // Concept clicked from sidebar
        const clickedConcept = ref({
            name: null,
            x: 0,
            y: 0,
        });
        // Concept clicked from storyboard
        const clickedStoryboardConcept = ref({
            name: null,
            x: 0,
            y: 0,
            dimmed: false,
        });
        // Concepts ignored from the sidebar
        const ignoredConceptNames = ref(props.currentAnalysis.ignored_concepts ?? []);
        const ignoredConcepts = computed(() => {
            return allConcepts.value.filter((c) => {
                if (hideMappedSidebar.value &&
                    mappedConcepts.value.includes(c.name)) {
                    return false;
                }
                return ignoredConceptNames.value.includes(c.name);
            });
        });
        // Save ignored concepts to analysis
        watch(ignoredConceptNames, (newVal, oldVal) => {
            if (isEqual(newVal, oldVal))
                return;
            updateStats();
            ProjectAPI.updateAnalysisDisplayAttributes(store, {
                ignored_concepts: newVal,
            });
        });
        const { fetch } = useFetchData();
        // Static saved queries fetched from Botanic
        const savedQueries = reactive({ value: [] });
        // Staged Queries Lists:
        //
        // Any changes to existing themes or new themes will be first reflected in the
        // staged lists below.
        // - 'stagedSavedQueries': List of Queries that initially reflect the savedQueries.
        //     Any unsaved changes made to the savedQueries exist in this list.
        // - 'stagedNewQueries': List of Any new unsaved Queries that are added via the ThemeBuilder.
        // - 'stagedQueries': List concatenation of `stagedNewQueries` and `stagedSavedQueries`.
        //      This list also acts as the base list for the sidebar.
        const stagedSavedQueries = reactive({ value: [] });
        const stagedNewQueries = reactive({ value: [] });
        const stagedQueries = computed(() => stagedSavedQueries.value
            .concat(stagedNewQueries.value)
            .sort((a, b) => a.name > b.name ? 1 : -1));
        // Current query
        const selectedQueryID = ref(null);
        const selectedQuery = computed({
            get() {
                return stagedQueries.value.find((q) => q.id === selectedQueryID.value) ?? null;
            },
            set(value) {
                if (!value)
                    return;
                const replaceId = (arr) => {
                    return arr.map((q) => {
                        if (q.id === value.id) {
                            return value;
                        }
                        return q;
                    });
                };
                if (value.is_new) {
                    stagedNewQueries.value = replaceId(stagedNewQueries.value);
                }
                else {
                    stagedSavedQueries.value = replaceId(stagedSavedQueries.value);
                }
            }
        });
        const selectedQueryScope = computed(() => selectedQuery.value?.query_value.level ?? 'frame');
        watch(selectedQueryScope, (newVal, oldVal) => {
            if (newVal && newVal !== oldVal) {
                updateStats();
            }
        });
        const hideMappedSidebar = ref(false);
        // All concepts that exist within a theme
        const mappedConcepts = computed(() => {
            return stagedQueries.value.reduce((acc, q) => {
                const concepts = getConceptsFromQuery(q);
                return acc.concat(concepts);
            }, []);
        });
        // Key concepts for the sidebar, optionally filtering out mapped concepts
        const keyConcepts = computed(() => {
            return allConcepts.value.filter((c) => {
                if (hideMappedSidebar.value &&
                    mappedConcepts.value.includes(c.name)) {
                    return false;
                }
                return !ignoredConceptNames.value.includes(c.name);
            });
        });
        // Current query in QueryRow format
        const selectedQueryRows = ref([]);
        // Update coverage if selectedQueryRows changes
        watch(selectedQueryRows, () => {
            if (selectedQueryID.value != null) {
                updateStats();
            }
        });
        // Populate selectedQueryRows when theme is selected
        watch(selectedQueryID, (newVal) => {
            if (newVal == null || !selectedQuery.value?.query_value) {
                selectedQueryRows.value = [];
                return;
            }
            const rows = getQueryRows(selectedQuery.value.query_value);
            selectedQueryRows.value = rows;
        });
        // Current query in Botanic format
        const selectedBotanicQuery = computed(() => {
            return QueryUtils.queryRowsToBotanic(selectedQueryRows.value, selectedQueryScope.value);
        });
        const selectedExpandedBotanicQuery = computed(() => {
            return expandQueryIfPossible(selectedBotanicQuery.value, savedQueries.value);
        });
        // Track changes to stagedQueries
        const hasChanges = computed(() => {
            const hasChanged = (q1, q2) => {
                return (!isEqual(q1.query_value, q2.query_value) ||
                    !isEqual(q1.name, q2.name) ||
                    !isEqual(q1.description, q2.description) ||
                    !isEqual(q1.dashboard_ids, q2.dashboard_ids));
            };
            return stagedQueries.value.reduce((map, query) => {
                const savedQuery = savedQueries.value.find((q) => q.id === query.id);
                map[query.id] = savedQuery != null
                    ? hasChanged(query, savedQuery)
                    : true;
                return map;
            }, {});
        });
        const isUnsaved = computed(() => {
            return Object.values(hasChanges.value).some((v) => v);
        });
        const stagedQueryIsValid = computed(() => {
            return isQueryValid(selectedQueryRows.value);
        });
        // Theme name modal
        const renameFormSubmit = async (query, newTheme = false) => {
            if (!selectedQuery.value)
                return;
            if (newTheme && !isNewTheme.value) {
                addTheme(selectedQuery.value);
                analytics?.track.themeBuilder.saveAsFromTheme(selectedQuery.value.id, query.name);
            }
            if (newTheme) {
                selectedQuery.value = {
                    ...selectedQuery.value,
                    ...query,
                };
            }
            let closeModal = true;
            await saveCurrentTheme(newTheme ? undefined : query)
                .catch((res) => {
                store.dispatch(CLEAR_REQUEST_ERRORS);
                submitErrors.value = res.body?.non_field_errors ?? [];
                closeModal = false;
            });
            if (closeModal) {
                showThemeNameModal.value = false;
            }
            if (!newTheme) {
                analytics?.track.themeBuilder.renameTheme(query.id, query.name);
            }
        };
        const dashboardFormSubmit = async (query) => {
            let closeModal = true;
            await saveCurrentTheme(query)
                .catch((res) => {
                store.dispatch(CLEAR_REQUEST_ERRORS);
                submitErrors.value = res.body?.non_field_errors ?? [];
                closeModal = false;
            });
            if (closeModal) {
                showDashboardModal.value = false;
            }
        };
        // Reset errors on modal close
        watch(() => showThemeNameModal.value, (newVal) => {
            if (!newVal) {
                submitErrors.value = [];
            }
        });
        const canAddToDashboard = computed(() => {
            return hasUnstructured(expandQuery('query', selectedBotanicQuery.value, savedQueries.value));
        });
        const npsFieldName = computed(() => {
            const schema = props.currentProject.schema;
            const field = schema.find((field) => field.typename === 'NPS');
            return field?.name;
        });
        const isNewTheme = computed(() => !!selectedQuery.value?.is_new);
        const allConcepts = computed(() => {
            return props.currentModel.topics_list;
        });
        const visibleConceptStrings = computed(() => {
            return allConcepts.value.filter(c => !ignoredConceptNames.value.includes(c.name)).map((c) => c.name).sort((c1, c2) => c1.localeCompare(c2));
        });
        const progressCoverage = computed(() => {
            const data = fetchedData['progress-coverage']?.data;
            return Math.round(data?.frames_covered / data?.all_frames * 100) || 0;
        });
        const showAddConceptAsOR = computed(() => {
            return countTextRows(selectedQueryRows.value) === 1;
        });
        const queryUpdatedMethod = computed(() => {
            return selectedQuery.value === null
                ? (val) => {
                    addTheme();
                    setQuery(val);
                }
                : setQuery;
        });
        /** Component methods **/
        const addConceptToQuery = (concept) => {
            let newRow = {
                type: 'text',
                values: [concept],
                operator: 'includes',
            };
            queryUpdatedMethod.value(selectedQueryRows.value.concat(newRow));
            scrollToTop();
            clearClickedConcept();
        };
        const verbatimsConfigChanged = (updated) => {
            verbatimsConfig.value = updated;
        };
        const addConceptAsOR = (concept) => {
            const rows = selectedQueryRows.value.map(r => {
                if (r.type !== 'text')
                    return r;
                r.values = (r.values ?? []).concat([concept]);
                return r;
            });
            setQuery(rows);
            scrollToTop();
        };
        const changeQueryScope = (newScope) => {
            stagedQueries.value.map((q) => {
                if (q.id === selectedQueryID.value)
                    q.query_value.level = newScope;
            });
        };
        const replaceQueryWithConcept = (concept) => {
            let newRow = {
                type: 'text',
                values: [concept],
                operator: 'includes',
            };
            setQuery([newRow]);
            scrollToTop();
        };
        const loadThemes = (overwriteStaged = true) => {
            isLoading.themes = true;
            Object.assign(savedQueries, []);
            return fetchThemes(props.projectId, props.analysisId)
                .then((res) => {
                savedQueries.value = cloneDeep(res);
                store.commit(SET_SAVED_QUERIES, cloneDeep(res));
                if (overwriteStaged) {
                    stagedSavedQueries.value = cloneDeep(res);
                }
                return updateStats();
            })
                .catch(() => error.value = ERROR.FETCH_THEMES)
                .finally(() => isLoading.themes = false);
        };
        const loadCoverage = () => {
            if (props.featureFlags.theme_groups) {
                return loadCoverageV2();
            }
            return loadCoverageV1();
        };
        const loadCoverageV1 = () => {
            isLoading.coverage = true;
            const validQueries = stagedQueries.value
                .filter(({ query_value }) => {
                const rows = getQueryRows(query_value);
                return isQueryValid(rows);
            });
            const fetchParams = [
                props.projectId,
                validQueries,
                props.currentAnalysis.topic_framework_id,
                props.currentSite.chrysalis_url,
                props.currentProject.chrysalis_ref,
            ];
            const cacheKey = { name: 'coverage', fetchParams };
            return fetch(cacheKey, Query.getThemeStats, fetchParams)
                .then(async (result) => {
                fetchedData['progress-coverage'] = {
                    status: 'done',
                    error: null,
                    data: {
                        all_frames: result?.all_frames,
                        frames_covered: result?.frames_covered,
                    },
                };
                fetchedData['themes-coverage'] = {
                    status: 'done',
                    error: null,
                    data: cloneDeep(result.query_stats),
                };
            })
                .catch((error) => {
                fetchedData['progress-coverage'] = {
                    status: 'done',
                    error,
                    data: null,
                };
                fetchedData['themes-coverage'] = {
                    status: 'done',
                    error,
                    data: null,
                };
            })
                .finally(() => isLoading.coverage = false);
        };
        const loadCoverageV2 = async () => {
            isLoading.coverage = true;
            const validQueries = stagedSavedQueries.value
                .filter(({ query_value }) => {
                const rows = getQueryRows(query_value);
                return isQueryValid(rows);
            });
            const expandedQueries = validQueries.map(q => {
                const expandedValue = expandQuery(q.name, q.query_value, validQueries);
                return {
                    name: q.name,
                    omit_from_overall: !hasUnstructured(expandedValue),
                    value: expandedValue,
                };
            });
            const { group_tree } = await ThemeGroup.list(props.projectId, props.analysisId);
            const mapGroupTreeToCoveragePayload = (coverageNodes) => {
                return coverageNodes.map((node) => {
                    const treeNode = {
                        name: node.name,
                        id: node.id,
                        type: node.type,
                        omit_from_overall: false,
                    };
                    if (node.type === 'group' && node.children) {
                        treeNode.children = mapGroupTreeToCoveragePayload(node.children);
                    }
                    if (node.type === 'theme') {
                        const query = expandedQueries.find((q) => q.name === node.name);
                        treeNode.value = query?.value;
                        treeNode.omit_from_overall = !hasUnstructured(query?.value);
                    }
                    return treeNode;
                });
            };
            let payload = mapGroupTreeToCoveragePayload(group_tree);
            // Remove top-level themes and add to ungrouped themes
            const ungroupedThemes = [];
            payload = payload.filter((node) => {
                if (node.type === 'theme') {
                    ungroupedThemes.push(node);
                    return false;
                }
                return true;
            });
            // Append Ungrouped themes group
            payload.push({
                id: -1,
                name: 'Ungrouped themes',
                omit_from_overall: false,
                type: 'group',
                children: ungroupedThemes,
            });
            const validNewQueries = stagedNewQueries.value
                .filter(({ query_value }) => {
                const rows = getQueryRows(query_value);
                return isQueryValid(rows);
            });
            validNewQueries.forEach((q) => {
                const value = expandQuery(q.name, q.query_value, validQueries);
                payload.push({
                    id: q.id,
                    name: q.name,
                    omit_from_overall: !hasUnstructured(value),
                    value: value,
                    type: 'theme',
                });
            });
            const fetchParams = [
                props.projectId,
                payload,
                props.currentAnalysis.topic_framework_id,
                props.currentSite.chrysalis_url,
                props.currentProject.chrysalis_ref,
            ];
            const cacheKey = { name: 'coverageV2', fetchParams };
            return fetch(cacheKey, Query.getThemeStatsV2, fetchParams)
                .then(async (result) => {
                const mapResult = (coverageNodes) => {
                    return coverageNodes.map((node) => {
                        const treeNode = {
                            ...node,
                            children: node.children ? mapResult(node.children) : undefined,
                        };
                        if (node.type === 'theme') {
                            const newTheme = stagedNewQueries.value.find((s) => s.name === node.name);
                            if (newTheme)
                                treeNode.is_new = true;
                        }
                        return treeNode;
                    });
                };
                const query_stats = mapResult(result.query_stats);
                fetchedData['progress-coverage'] = {
                    status: 'done',
                    error: null,
                    data: {
                        all_frames: result?.all_frames,
                        frames_covered: result?.frames_covered,
                    },
                };
                fetchedData['themes-coverage'] = {
                    status: 'done',
                    error: null,
                    data: query_stats,
                };
            })
                .catch((error) => {
                fetchedData['progress-coverage'] = {
                    status: 'done',
                    error,
                    data: null,
                };
                fetchedData['themes-coverage'] = {
                    status: 'done',
                    error,
                    data: null,
                };
            })
                .finally(() => isLoading.coverage = false);
        };
        const loadIgnoredConceptsStats = () => {
            if (ignoredConceptNames.value.length === 0) {
                return;
            }
            isLoading.ignoredConceptsStats = true;
            const queries = ignoredConceptNames.value.map((c) => {
                return {
                    name: c,
                    query_value: {
                        type: "match_all",
                        includes: [
                            {
                                type: "text",
                                value: c,
                            },
                            {
                                type: "segment",
                                field: "Token Count",
                                operator: "<=",
                                value: "2"
                            }
                        ]
                    }
                };
            });
            const fetchParams = [
                props.projectId,
                queries,
                props.currentAnalysis.topic_framework_id,
                props.currentSite.chrysalis_url,
                props.currentProject.chrysalis_ref,
            ];
            const cacheKey = { name: 'ignoredConceptsStats', fetchParams };
            return fetch(cacheKey, Query.getThemeStats, fetchParams)
                .then((result) => {
                fetchedData['ignored-concepts-stats'] = {
                    status: 'done',
                    error: null,
                    data: result?.query_stats.map((c) => {
                        return {
                            name: c.name,
                            frequency: c.num_hits,
                            nonEmptyCoverage: c.coverage,
                        };
                    }),
                };
            })
                .catch((error) => {
                fetchedData['ignored-concepts-stats'] = {
                    status: 'done',
                    error,
                    data: null,
                };
            }).finally(() => isLoading.ignoredConceptsStats = false);
        };
        // Select currently focused theme
        const selectTheme = (id) => {
            selectedQueryID.value = id;
        };
        // Delete row at index of staged query
        const deleteRow = (index) => {
            const rows = selectedQueryRows.value.filter((_, i) => i !== index);
            setQuery(rows);
        };
        // Set staged query
        const setQuery = (rows) => {
            if (!selectedQuery.value)
                return;
            selectedQueryRows.value = rows;
            const botanic = QueryUtils.queryRowsToBotanic(rows, selectedQueryScope.value);
            selectedQuery.value = {
                ...selectedQuery.value,
                query_value: botanic,
            };
        };
        const addTheme = (copy = {}) => {
            const newTheme = {
                name: makeNewThemeName(stagedQueries.value),
                description: '',
                query_value: {
                    level: props.currentProject.query_scope_default ?? 'frame',
                },
                dashboard_ids: [],
                ...copy,
                analysis: props.analysisId,
                project: props.projectId,
                created: '',
                modified: '',
                is_new: true,
                id: Date.now(),
                exclude_mapped: false,
            };
            stagedNewQueries.value.push(newTheme);
            selectTheme(newTheme.id);
        };
        const duplicateTheme = () => {
            if (!selectedQuery.value)
                return;
            showThemeNameModal.value = 'new_theme';
        };
        const deleteTheme = async (theme, conflicts = []) => {
            const removeOrReplaceState = (theme, replace) => {
                if (theme.is_new) {
                    const index = stagedNewQueries.value.findIndex((q) => q.id === theme.id);
                    replace
                        ? stagedNewQueries.value.splice(index, 1, replace)
                        : stagedNewQueries.value.splice(index, 1);
                }
                else {
                    let index = stagedSavedQueries.value.findIndex((q) => q.id === theme.id);
                    replace
                        ? stagedSavedQueries.value.splice(index, 1, replace)
                        : stagedSavedQueries.value.splice(index, 1);
                    index = savedQueries.value.findIndex((q) => q.id === theme.id);
                    replace
                        ? savedQueries.value.splice(index, 1, replace)
                        : savedQueries.value.splice(index, 1);
                }
                selectedQueryID.value = null;
            };
            // Update queries that contain the query to be deleted
            const requests = conflicts.map(async (conflict) => {
                const level = conflict.query_value.level ? conflict.query_value.level : 'frame';
                const rows = QueryUtils.botanicToQueryRows(conflict.query_value);
                rows.forEach((row, i) => {
                    if (row.type === 'query') {
                        row.values = row.values?.filter((v) => v !== theme.id.toString());
                        if (!row.values?.length) {
                            rows.splice(i, 1);
                        }
                    }
                });
                return rows.length === 0
                    ? Query.deleteSavedQuery(conflict.project, conflict.analysis, conflict.id)
                        .then(() => {
                        removeOrReplaceState(conflict);
                    })
                    : Query.updateSavedQueryV2(conflict.project, conflict.analysis, {
                        query_value: QueryUtils.queryRowsToBotanic(rows, level),
                        id: conflict.id,
                    })
                        .then(() => {
                        removeOrReplaceState(conflict, {
                            ...conflict,
                            query_value: QueryUtils.queryRowsToBotanic(rows, level),
                        });
                    });
            });
            await Promise.all(requests);
            showThemeDeleteModal.value = false;
            if (isNewTheme.value) {
                removeOrReplaceState(theme);
            }
            else {
                await Query.deleteSavedQuery(props.projectId, props.analysisId, theme.id).then(() => {
                    removeOrReplaceState(theme);
                    updateStats();
                    analytics?.track.themeBuilder.deleteTheme(theme.id);
                });
            }
        };
        const resetButtonClick = async () => {
            const confirm = await resetConfirmModal.value?.confirm();
            if (confirm) {
                analytics?.track.themeBuilder.discardAllChanges();
                resetState(false);
            }
        };
        const resetState = async (resetNew = true) => {
            queryStats.record_count = 0;
            queryStats.total_record_count = 0;
            queryStats.verbatim_count = 0;
            queryStats.total_verbatim_count = 0;
            selectedQueryID.value = null;
            stagedSavedQueries.value = [];
            if (resetNew)
                stagedNewQueries.value = [];
            await loadThemes();
        };
        const resetCurrentTheme = () => {
            const id = selectedQueryID.value;
            const savedQuery = savedQueries.value.find((q) => q.id === id);
            if (!savedQuery || !selectedQuery.value)
                return;
            const rows = getQueryRows(savedQuery.query_value);
            setQuery(rows);
            selectedQuery.value = {
                ...selectedQuery.value,
                name: savedQuery.name,
                query_value: {
                    ...selectedQuery.value.query_value,
                    level: savedQuery.query_value.level,
                },
            };
        };
        // Save all saved themes with changes
        const saveAllThemes = () => {
            const requests = stagedQueries.value.map((q) => {
                return hasChanges.value[q.id]
                    ? saveOrUpdateTheme(q)
                    : Promise.resolve();
            });
            return Promise.all(requests);
        };
        const saveCurrentTheme = (partial) => {
            if (!selectedQuery.value)
                return Promise.reject();
            return saveOrUpdateTheme(selectedQuery.value, partial);
        };
        // If partial payload is provided, only those fields will be updated,
        // leaving currently staged changes to the current query unsaved
        const saveOrUpdateTheme = (query, partial) => {
            const rows = getQueryRows(query.query_value);
            // Bail if invalid query
            if (!isQueryValid(rows)) {
                return Promise.resolve();
            }
            const saveMethod = query.is_new
                ? Query.createSavedQueryV2
                : Query.updateSavedQueryV2;
            const payload = {
                ...query,
                ...partial ?? {},
            };
            return saveMethod(props.projectId, props.analysisId, payload)
                .then(async (res) => {
                // Track create/save event
                if (query.is_new) {
                    analytics?.track.themeBuilder.createTheme(query.name, query.id);
                }
                else {
                    analytics?.track.themeBuilder.saveTheme(query.name, query.id);
                }
                // Remove newly saved theme from stagedNewQueries
                // NOTE: The id returned from botanic in response is
                // different from the id assigned to query by frontend,
                // so we compare using the local `query.id` instead.
                stagedNewQueries.value =
                    stagedNewQueries.value
                        .filter((q) => q.id !== query.id);
                // Refresh savedQueries
                loadThemes(false);
                // Insert new theme into stagedSavedQueries
                const savedQuery = res.body ?? res;
                const index = stagedSavedQueries.value.findIndex((q) => q.id === savedQuery.id);
                if (index !== -1) {
                    stagedSavedQueries.value[index] = partial
                        ? {
                            ...stagedSavedQueries.value[index],
                            ...partial,
                        }
                        : savedQuery;
                }
                else {
                    stagedSavedQueries.value.push(savedQuery);
                }
                // Replace selectedQueryID with botanic savedQuery.id
                // if the selected query is being saved
                selectedQueryID.value = query.id === selectedQueryID.value ?
                    savedQuery.id :
                    selectedQueryID.value;
            });
        };
        const fetchWidgetData = (type) => {
            return (name, params) => {
                fetchedData[name] = {
                    status: 'fetching',
                    error: null,
                    data: null,
                };
                let fetchFn = fetch_pivot_data;
                let fetchParams = [];
                if (type === 'key_phrases') {
                    fetchFn = fetch_keyphrases_data;
                    fetchParams = [
                        {
                            projectId: props.projectId,
                            chrysalisRef: props.currentProject.chrysalis_ref,
                            chrysalis_url: props.currentSite.chrysalis_url,
                            topicId: props.currentAnalysis.topic_framework_id,
                        },
                        params,
                    ];
                }
                else if (type === 'context_network') {
                    const baselineQuery = QueryUtils.extractStructuredFiltersFromQuery(params.query);
                    fetchFn = Query.generateContextNetwork;
                    fetchParams = [
                        props.projectId,
                        props.analysisId,
                        props.currentSite.chrysalis_url,
                        props.currentProject.chrysalis_ref,
                        props.currentAnalysis.topic_framework_id,
                        params.query,
                        savedQueries.value,
                        {
                            ...params.options,
                            baseline_query: JSON.stringify(baselineQuery)
                        },
                    ];
                }
                else if (type === 'verbatims') {
                    fetchFn = Query.runQuery;
                    fetchParams = [
                        props.projectId,
                        props.analysisId,
                        params.query,
                        savedQueries.value,
                        {
                            ...params.options,
                        }
                    ];
                }
                const cacheKey = { fetchParams, name };
                fetch(cacheKey, fetchFn, fetchParams)
                    .then((result) => {
                    fetchedData[name] = {
                        status: 'done',
                        error: null,
                        data: result,
                    };
                })
                    .catch((error) => {
                    fetchedData[name] = {
                        status: 'done',
                        error,
                        data: null,
                    };
                });
            };
        };
        const fetchVerbatimCount = () => {
            const query = {
                includes: [
                    selectedExpandedBotanicQuery.value,
                    { type: 'nonempty_data' },
                ],
                excludes: [],
                type: "match_all",
            };
            const countFetchParams = [
                props.projectId,
                props.analysisId,
                query,
                savedQueries.value,
                {
                    start: 0,
                    limit: 0,
                },
            ];
            const countCacheKey = { countFetchParams, name: 'verbatim_count' };
            return Promise.all([
                fetch(countCacheKey, Query.runQuery, countFetchParams)
                    .then((result) => {
                    const count = result.count;
                    queryStats.verbatim_count = count;
                }),
            ]);
        };
        const fetchIgnoredVerbatimCount = () => {
            const validStagedQueries = stagedQueries.value.reduce((arr, q) => {
                const rows = getQueryRows(q.query_value);
                if (!isQueryValid(rows))
                    return arr;
                // Convert to botanic format, this process excludes any rows with
                // empty values that would otherwise error if sent to Chrysalis
                const query_value = QueryUtils.queryRowsToBotanic(rows, q.query_value.level ?? 'frame');
                return arr.concat({
                    ...q,
                    query_value,
                });
            }, []);
            const base_query = {
                "level": "frame",
                "type": "match_all",
                "includes": [
                    {
                        type: 'match_any',
                        includes: ignoredConceptNames.value.map((c) => ({
                            type: 'text',
                            value: c,
                        })),
                    },
                    {
                        type: 'match_any',
                        includes: [{
                                type: "segment",
                                field: 'Token Count',
                                operator: '<=',
                                value: "2"
                            }]
                    },
                ],
            };
            const exclude_queries_list = validStagedQueries.map((q) => {
                return {
                    "name": q.name,
                    "value": expandSavedQueryIfPossible(q.name, q.query_value, savedQueries.value)
                };
            });
            const unmappedBody = {
                "base_query": base_query,
                "exclude_queries_list": exclude_queries_list,
            };
            const countFetchParams = [
                props.projectId,
                unmappedBody,
                props.currentAnalysis.topic_framework_id,
                props.currentSite.chrysalis_url,
                props.currentProject.chrysalis_ref,
                {
                    start: 0,
                    limit: 0,
                    documents: false,
                    networkModel: false,
                },
            ];
            const totalFetchParams = [
                props.projectId,
                props.analysisId,
                {
                    type: 'match_all',
                    includes: [
                        { type: 'all_data' },
                        { type: 'nonempty_data' },
                    ]
                },
                savedQueries.value,
                {
                    start: 0,
                    limit: 0,
                },
            ];
            const countCacheKey = { countFetchParams, name: 'ignored_verbatim_count' };
            const totalCacheKey = { countFetchParams, name: 'total_verbatim_count' };
            const fetchCoverage = ignoredConceptNames.value.length === 0
                ? Promise.resolve({ count: 0 })
                : fetch(countCacheKey, Query.runUnmapped, countFetchParams);
            return Promise.all([
                fetchCoverage
                    .then((result) => {
                    const count = result.total_hits;
                    queryStats.ignored_verbatim_count = count;
                }),
                fetch(totalCacheKey, Query.runQuery, totalFetchParams)
                    .then((result) => {
                    const count = result.count;
                    queryStats.total_verbatim_count = count;
                }),
            ]);
        };
        const fetchRecordCount = () => {
            isLoading.stats = true;
            const requirements = {
                blocks: [{
                        aggfuncs: [{
                                new_column: 'frequency_cov',
                                src_column: 'document_id',
                                aggfunc: 'count',
                            }],
                    }],
                queries: [{
                        name: 'filtered_query',
                        value: selectedExpandedBotanicQuery.value,
                    }],
            };
            const fetchParams = [
                {
                    projectId: props.projectId,
                    chrysalisRef: props.currentProject.chrysalis_ref,
                    chrysalis_url: props.currentSite.chrysalis_url,
                    topicId: props.currentAnalysis.topic_framework_id,
                },
                requirements,
            ];
            const cacheKey = { fetchParams, name: 'record_count' };
            return fetch(cacheKey, fetch_pivot_data, fetchParams)
                .then((result) => {
                const count = result.payload.find((d) => d.group__ === 'filtered_query')?.frequency_cov;
                const overall = result.payload.find((d) => d.group__ === 'overall__')?.frequency_cov;
                queryStats.record_count = count;
                queryStats.total_record_count = overall;
                isLoading.stats = false;
            });
        };
        const updateStats = async () => {
            // Fetch new data
            await Promise.all([
                loadCoverage(),
                stagedQueryIsValid.value && fetchVerbatimCount(),
                stagedQueryIsValid.value && fetchRecordCount(),
                loadIgnoredConceptsStats(),
                fetchIgnoredVerbatimCount(),
            ]);
        };
        const addConceptFromSidebar = (concept, newTheme) => {
            if (newTheme) {
                addTheme();
                setQuery([{
                        type: 'text',
                        values: [concept],
                        operator: 'includes',
                    }]);
            }
            else {
                addConceptToQuery(concept);
            }
            clearClickedConcept();
            scrollToTop();
        };
        const sidebarConceptClicked = (concept, el) => {
            const { x, y } = el.getBoundingClientRect();
            clickedConcept.value = {
                name: concept,
                x: x + 10,
                y: y,
            };
        };
        const storyboardConceptClicked = (concept, el, dimmed) => {
            const { x, y } = el.getBoundingClientRect();
            clickedStoryboardConcept.value = {
                name: concept,
                x: x + 10,
                y: y,
                dimmed,
            };
        };
        const ignoreConcept = (concept) => {
            ignoredConceptNames.value = ignoredConceptNames.value.concat([concept]);
            clearClickedConcept();
            analytics?.track.themeBuilder.ignoreConcept(concept);
        };
        const unignoreConcept = (concept) => {
            ignoredConceptNames.value = ignoredConceptNames.value.filter((c) => c !== concept);
            clearClickedConcept();
        };
        const clearClickedConcept = () => {
            clickedConcept.value = {
                name: null,
                x: 0,
                y: 0,
            };
            clickedStoryboardConcept.value = {
                name: null,
                x: 0,
                y: 0,
                dimmed: false,
            };
        };
        const scrollToTop = () => {
            scrollableContent.value?.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth',
            });
        };
        const setExcludeMapped = (value) => {
            selectedQuery.value = {
                ...selectedQuery.value,
                exclude_mapped: value,
            };
        };
        const goToUnmappedPage = (concept) => {
            analytics?.track.themeBuilder.viewOnUnmapped(concept);
            router.push({
                name: 'unmapped',
                params: {
                    projectId: props.projectId.toString(),
                    analysisId: props.analysisId.toString(),
                    navigatedConcept: concept,
                },
            });
        };
        const updateTree = async () => {
            await updateStats();
        };
        /** Lifecyle hooks **/
        onMounted(async () => {
            await resetState();
            isLoading.state = false;
        });
        return {
            error,
            savedQueries,
            selectTheme,
            isLoading,
            activeTab,
            addConceptToQuery,
            addConceptAsOR,
            replaceQueryWithConcept,
            deleteRow,
            setQuery,
            addTheme,
            changeQueryScope,
            resetState,
            fetchedData,
            hasFiles: true,
            fetchContextNetwork: fetchWidgetData('context_network'),
            fetchKeyPhrases: fetchWidgetData('key_phrases'),
            fetchVerbatims: fetchWidgetData('verbatims'),
            npsFieldName,
            showJSON,
            isNewTheme,
            queryUpdatedMethod,
            saveOrUpdateTheme,
            saveCurrentTheme,
            resetCurrentTheme,
            deleteTheme,
            stagedQueries,
            hasChanges,
            selectedQuery,
            selectedQueryID,
            selectedQueryRows,
            selectedBotanicQuery,
            selectedExpandedBotanicQuery,
            progressCoverage,
            showAddConceptAsOR,
            stagedQueryIsValid,
            allConcepts,
            visibleConceptStrings,
            queryStats,
            showThemeNameModal,
            canAddToDashboard,
            renameFormSubmit,
            submitErrors,
            showThemeDeleteModal,
            showDashboardModal,
            dashboardFormSubmit,
            duplicateTheme,
            percent,
            sidebarConceptClicked,
            storyboardConceptClicked,
            clickedConcept,
            clickedStoryboardConcept,
            clearClickedConcept,
            addConceptFromSidebar,
            ignoreConcept,
            unignoreConcept,
            ignoredConcepts,
            ignoredConceptNames,
            isUnsaved,
            selectedQueryScope,
            scrollableContent,
            comma,
            resetConfirmModal,
            resetButtonClick,
            setExcludeMapped,
            hasNoResults,
            hideMappedSidebar,
            keyConcepts,
            goToUnmappedPage,
            verbatimsConfig,
            verbatimsConfigChanged,
            saveAllThemes,
            location: QueryLocation.ThemeBuilder,
            updateStats,
            updateTree,
        };
    }
});
export default ThemeBuilder;
